import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Loader } from '../components/ui/Loader';
import { TemporaryNumberCard } from '../components/temporary-numbers/TemporaryNumberCard';
import { useSse } from '../contexts/SseContext';
import { useGetTemporaryNumbersQuery } from '../store/api/temporaryNumbersApi';

type StatusFilter = 'all' | 'expired' | 'refunded' | 'resendable' | 'waiting';

export function TemporaryNumbersListPage() {
  const { t } = useTranslation();
  const { receivedCodes, isConnected } = useSse();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, error, refetch } = useGetTemporaryNumbersQuery({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    page,
    limit,
  });

  // Refetch on mount to catch newly purchased numbers (e.g. Durian numbers without codes yet)
  useEffect(() => {
    refetch();
  }, [refetch]);

  // Refetch when a code is received via SSE
  useEffect(() => {
    if (isConnected && receivedCodes.size > 0) {
      // Debounce refetch to avoid too many requests
      const timeoutId = setTimeout(() => {
        void refetch();
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [receivedCodes.size, isConnected, refetch]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const numbers = data?.data.numbers || [];
  const pagination = data?.data.pagination;

  const filteredNumbers = useMemo(() => {
    if (!searchQuery.trim()) return numbers;
    const q = searchQuery.trim().toLowerCase();
    return numbers.filter(
      (n) =>
        n.number.toLowerCase().includes(q) ||
        n.serviceName.toLowerCase().includes(q) ||
        n.countryName?.toLowerCase().includes(q)
    );
  }, [numbers, searchQuery]);

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('errors.network')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } })
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <Link
                to="/temporary-numbers"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('common.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('temporaryNumbers.list.title')}
            </h1>
          </div>

          {/* Filters */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 mb-6 animate-scale-in">
            <div className="flex flex-col gap-4">
              {/* Search */}
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-medium pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  placeholder={t('temporaryNumbers.list.searchPlaceholder')}
                  className="w-full pl-10 pr-10 py-2.5 bg-neutral-dark/50 border border-neutral-dark rounded-lg text-neutral-light font-body placeholder:text-neutral-medium focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent transition-all duration-300"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-medium hover:text-neutral-light transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Status Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-neutral-medium font-body">
                  {t('temporaryNumbers.list.filter')}:
                </span>
                {(['all', 'expired', 'refunded', 'resendable', 'waiting'] as StatusFilter[]).map(
                  (status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-lg font-heading font-semibold transition-all duration-300 ${
                        statusFilter === status
                          ? 'bg-brand-accent text-brand-primary'
                          : 'bg-neutral-dark text-neutral-light hover:bg-neutral-dark/80'
                      }`}
                    >
                      {status === 'all'
                        ? t('common.all')
                        : t(`temporaryNumbers.status.${status}`)}
                    </button>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Numbers List */}
          <div className="mb-6 animate-scale-in p-0 md:card-glass md:rounded-2xl md:shadow-2xl md:p-6 md:p-8">
            {filteredNumbers.length === 0 ? (
              <div className="text-center py-12 px-4 md:px-0">
                <p className="text-neutral-medium text-lg font-body mb-4">
                  {searchQuery.trim()
                    ? t('temporaryNumbers.list.noResults')
                    : t('temporaryNumbers.list.empty')}
                </p>
                {!searchQuery.trim() && (
                  <Link
                    to="/temporary-numbers"
                    className="inline-block px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                  >
                    {t('temporaryNumbers.purchase.title')}
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredNumbers.map((number) => (
                  <TemporaryNumberCard key={number._id} temporaryNumber={number} />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.previous')}
              </button>
              <span className="text-neutral-medium font-body px-4">
                {t('common.page')} {pagination.page} {t('common.of')} {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pagination.pages}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

